<script setup lang="ts">
  import { computed, useId } from 'vue';
  import type { ComponentPublicInstance } from 'vue';

  import { DropzoneArea } from '@/features/Document';
  import { ColoredButton, IconImage } from '@/shared/ui';
  import PdfFileIcon from '@/shared/assets/icons/PdfFileIcon.svg';
  import PlusIcon from '@/shared/assets/icons/PlusIcon.svg';
  import TrashIcon from '@/shared/assets/icons/GrayTrashIcon.svg';
  import UploadSuccessIcon from '@/shared/assets/icons/UploadSuccessIcon.svg';

  const props = defineProps<{
    accept: string;
    isDragging: boolean;
    localFiles: File[];
    sizeError: string;
    isAtAttachmentLimit: boolean;
    setFileInputRef: (element: Element | ComponentPublicInstance | null) => void;
    onDragEnter: (event: DragEvent) => void;
    onDragLeave: (event: DragEvent) => void;
    onDragOver: (event: DragEvent) => void;
    onFilesDropped: (event: DragEvent) => void;
    onFilesSelected: (event: Event) => void;
    openFileDialog: () => void;
    removeFile: (index: number) => void;
  }>();

  const sectionId = useId();

  const errorId = `${sectionId}-error`;

  const errorDescribedBy = computed(() => (props.sizeError ? errorId : undefined));
</script>

<template>
  <section class="flex flex-col gap-4" data-testid="electronic-upload-section">
    <input
      :ref="setFileInputRef"
      type="file"
      multiple
      class="hidden"
      :accept="accept"
      data-testid="file-input"
      @change="onFilesSelected"
    />

    <DropzoneArea
      v-if="localFiles.length === 0"
      :is-dragging="isDragging"
      :on-drag-enter="onDragEnter"
      :on-drag-leave="onDragLeave"
      :on-drag-over="onDragOver"
      :on-drop="onFilesDropped"
      :open-file-dialog="openFileDialog"
      :multiple="true"
    />

    <ul
      v-if="localFiles.length > 0"
      class="w-full divide-y divide-gray-02 overflow-hidden rounded-xl border border-dashed border-gray-02"
      data-testid="file-list"
    >
      <li
        v-for="(file, index) in localFiles"
        :key="index"
        class="flex items-center gap-4 px-4 py-5"
        data-testid="file-list-item"
      >
        <div class="flex min-w-0 flex-1 items-center gap-3">
          <IconImage :src="PdfFileIcon" alt="Arquivo" class="shrink-0" />
          <span class="truncate text-sm text-gray-07">{{ file.name }}</span>
        </div>
        <div class="relative h-2 w-1/2 flex-shrink-0 overflow-hidden rounded-full bg-gray-02">
          <div class="absolute inset-y-0 left-0 h-full w-full rounded-full bg-green-19" />
        </div>
        <span class="min-w-[40px] text-right text-xs text-gray-07">100%</span>
        <IconImage :src="UploadSuccessIcon" alt="Upload concluído" class="h-5 w-5 flex-shrink-0" />
        <button
          type="button"
          class="flex-shrink-0 text-gray-03 hover:text-red-16"
          data-testid="remove-file-button"
          @click="removeFile(index)"
        >
          <IconImage :src="TrashIcon" alt="Remover arquivo" class="h-5 w-5" />
        </button>
      </li>
    </ul>

    <div v-if="localFiles.length > 0" class="flex justify-end">
      <ColoredButton
        :icon="PlusIcon"
        text="Adicionar documento"
        :disabled="isAtAttachmentLimit"
        :aria-describedby="errorDescribedBy"
        data-testid="add-document-button"
        @click="openFileDialog"
      />
    </div>

    <p
      v-if="sizeError"
      :id="errorId"
      class="text-xs text-red-16"
      role="alert"
      data-testid="file-error-message"
    >
      {{ sizeError }}
    </p>
  </section>
</template>
